import React from 'react'
import Spinner from 'library/atoms/Spinner'
import Price, { PriceProps } from 'shared/library/atoms/Price'
import { MutationResult } from '@apollo/client'
import { Basket_Response } from 'shared/presenters/graphqlTypes'
import useTranslation from 'next-translate/useTranslation'
import Flex from 'shared/library/atoms/Flex'
import { determineIsNotNullish } from 'shared/helpers/utilityTypes'

export interface BasketPriceInterface extends Pick<PriceProps, 'currency'> {
  priceLoading: MutationResult['loading']
  basketId?: Basket_Response['basket_id']
  pence?: Basket_Response['price_after_adjustments_pence']
  preAdjustmentsPence?: number
}

/**
 * Extraction as this logic was being repeated in multiple places
 *
 * We return null in cases where we do not pass a valid price into the component - this is useful in the cases where
 * we do not want to display anything at all i.e. when there is no price because the basket is empty.
 */
const BasketPrice = ({
  priceLoading,
  preAdjustmentsPence,
  pence,
  basketId,
  currency
}: BasketPriceInterface) => {
  const { t } = useTranslation('common')

  if (priceLoading) <Spinner />

  if (!(determineIsNotNullish(pence) && basketId)) return null

  const TOTAL_PRICE_SETTINGS = {
    pence,
    'aria-label': t('total'),
    fontStyle: 'h5' as const,
    currency
  }

  return preAdjustmentsPence && preAdjustmentsPence > pence ? (
    <Flex gap={3}>
      <Price
        strike={true}
        pence={preAdjustmentsPence}
        aria-label={t('pre-adjustments-total')}
        fontStyle="h6"
        color="tertiary"
        currency={currency}
      />
      <Price {...TOTAL_PRICE_SETTINGS} />
    </Flex>
  ) : (
    <Price {...TOTAL_PRICE_SETTINGS} />
  )
}
export default BasketPrice
